import type { AxiosInstancePlus } from '@tencent/oneid-request';
import { THROTTLE_DEFAULT_SECOND } from 'common/constants/Constants';
import type { IResponseType, TInvitationInfo } from 'common/constants/Types';
import { http } from 'common/http';
import { IAppsConfig } from 'common/http/bff/Apps';
import type { ICaptchaReportParams } from 'common/utils/captchaRequest';
import { newCaptchaRequest } from 'common/utils/captchaRequest';
import { TInvitationAccount } from 'idpBase/http/type';

/** 链接邀请状态枚举 */
export enum APPROVAL_RESULT_TYPES {
  /** 未填写 */
  INITIALIZED,
  /** 审批中 */
  PENDING,
  /** 已接受 */
  ACCEPTED,
  /** 已拒绝 */
  REJECTED,
}

export interface IRedirectInfo {
  /** 跳转链接 */
  redirect_url: string;
  /** 说明文案 */
  prompt_text: string;
  /** 按钮文案 */
  button_text: string;
  /** 邀请状态 */
  approval_status?: APPROVAL_RESULT_TYPES;
  /** 企业配置是否免验证 */
  invitation_status?: number;
  /** 用户是否已加入过企业 */
  exist_account_user?: boolean;
}

/** 邀请信息的返回 */
export interface IDeptInvitationResponse {
  invitation: TInvitationInfo;
  account: TInvitationAccount;
  account_user: { name: string };
  app: IAppsConfig;
  redirect_info: IRedirectInfo;
}

class AuthApi {
  public axiosInstance: AxiosInstancePlus = http;

  public prefixURL = '/v1/auth';

  /** 获取邀请信息 */
  public getDeptInvitation(params: {
    invitation_id?: string;
    inv_code?: string;
    app?: string;
  }): Promise<IResponseType<IDeptInvitationResponse>> {
    return this.axiosInstance.get(`${this.prefixURL}/contacts/departments/invitation`, {
      params,
      skipShowError: true,
    });
  }

  /** 发送加入申请 */
  public sendJoinRequest(params: {
    invitation_id?: string;
    invitation_code?: string;
    verification_code: string;
    state_token: string;
    user_name: string;
    app?: string;
  }): Promise<IResponseType<IRedirectInfo>> {
    return this.axiosInstance.post(`${this.prefixURL}/invitations/process`, params, {
      skipShowError: true,
    });
  }

  /** 发送验证码 */
  public smsCodeSend(
    data: {
      mobile?: string;
      is_re_verify?: boolean;
    },
    reportParams?: ICaptchaReportParams,
  ): Promise<IResponseType<{ state_token?: string; expires_in?: number; captcha?: any }>> {
    return newCaptchaRequest(
      async (captchaVerification) =>
        http.post(
          `${this.prefixURL}/invitations/sms:send`,
          { ...data, captchaVerification },
          {
            throttleConfig: { wait: THROTTLE_DEFAULT_SECOND },
          },
        ),
      reportParams,
    );
  }

  /** 验证二次校验的验证码 */
  reverifyCode(
    params: {
      invitation_id?: string;
      invitation_code?: string;
      verification_code: string;
      state_token: string;
    },
    configs?: Record<string, any>,
  ): Promise<IResponseType> {
    return http.post(`${this.prefixURL}/invitations/sms:reVerify`, params, configs);
  }

  /** 解绑账号 */
  unbindAccounts(): Promise<IResponseType> {
    return this.axiosInstance.post(`${this.prefixURL}/person/unbind_accounts`);
  }

  /** 获取解绑账号列表 */
  getUnbindAccounts(): Promise<IResponseType> {
    return this.axiosInstance.get(`${this.prefixURL}/person/unbind_accounts`);
  }

  /** 邀请加入成功后的前往使用地址 */
  invitationTakeEffect(params: { app?: string }): Promise<IResponseType<{ redirect_url: string }>> {
    return this.axiosInstance.post(`${this.prefixURL}/invitations/take_effect`, params, {
      skipShowError: true,
    });
  }

  /** 刷新邀请状态 */
  getInvitationStatus(
    params: {
      inv_id?: string;
      inv_code?: string;
    },
    configs?: Record<string, any>,
  ): Promise<IResponseType<{ invitation_status: APPROVAL_RESULT_TYPES }>> {
    return this.axiosInstance.get(
      `${this.prefixURL}/contacts/departments/invitations/approvals/status`,
      {
        params,
        ...(configs ?? {}),
      },
    );
  }
}

export default new AuthApi();
